"use client";

import React, { useEffect, useRef, useState } from "react";

interface AccountProfile {
	userName?: string;
	drops: number;
	snags: number;
}

interface MergeConfirmationModalProps {
	sourceProfile: AccountProfile;
	targetProfile: AccountProfile;
	sourceUserId: string;
	targetUserId: string;
	privyUserId?: string;
	onMergeSuccess: () => void;
	onCancel: () => void;
}

/**
 * Shown when privy-login returns a 409 mergeRequired response.
 * Displays both accounts' stats and asks the user to confirm the merge.
 */
export const MergeConfirmationModal: React.FC<MergeConfirmationModalProps> = ({
	sourceProfile,
	targetProfile,
	sourceUserId,
	targetUserId,
	privyUserId,
	onMergeSuccess,
	onCancel,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [merged, setMerged] = useState(false);
	const dialogRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		dialogRef.current?.focus();
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape" && !isLoading) {
				onCancel();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [onCancel, isLoading]);

	const handleMerge = async () => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await fetch("/api/user/auth/merge-accounts", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ sourceUserId, targetUserId, privyUserId }),
			});
			const data = await response.json();
			if (!response.ok || !data.status) {
				setError(data.message ?? "Merge failed. Please try again.");
				return;
			}
			setMerged(true);
		} catch {
			setError("Something went wrong. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
			<div
				ref={dialogRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby="merge-accounts-title"
				tabIndex={-1}
				className="bg-[#373132] p-6 rounded-2xl shadow-xl w-full max-w-md"
			>

				{merged ? (
					/* ── Success state ── */
					<>
						<div className="text-center mb-5">
							<div className="w-14 h-14 rounded-full bg-mgt-green/20 flex items-center justify-center mx-auto mb-3">
								<svg className="w-7 h-7 text-mgt-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
									<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
								</svg>
							</div>
							<h3 className="text-xl font-bold text-[#FFF1E9] mb-1">Accounts merged!</h3>
							<p className="text-[#918884] text-sm">
								Your drops, snags, and profile data are now combined.
							</p>
						</div>

						<div className="bg-[#231F20] rounded-xl p-4 mb-5 border border-yellow-500/20">
							<p className="text-yellow-400 text-xs font-semibold uppercase tracking-wide mb-1">Important — log in going forward</p>
							<p className="text-[#FFF1E9] text-sm">
							Always sign in using <span className="font-semibold text-[#E8C5A0]">this account</span> (the one you&apos;re currently in).
							Your old account&apos;s login method is now retired &mdash; both accounts&apos; data lives here.
							</p>
							<p className="text-[#918884] text-xs mt-2">
								If you accidentally log in with your old method, you will automatically be shown this merged account.
							</p>
						</div>

						<button
							onClick={onMergeSuccess}
							className="w-full px-5 py-2.5 rounded-lg bg-[#E8C5A0] text-[#231F20] font-semibold"
						>
							Got it
						</button>
					</>
				) : (
					/* ── Confirm state ── */
					<>
						<h3 id="merge-accounts-title" className="text-xl font-bold mb-2 text-[#FFF1E9]">
							Merge Accounts
						</h3>
						<p className="text-[#918884] mb-6 text-sm">
							We found an existing account with this data. Merge them so all your drops and snags appear in one place.
						</p>

						<div className="flex gap-3 mb-5">
							<div className="flex-1 bg-[#231F20] rounded-xl p-4">
								<p className="text-xs text-[#918884] mb-1 uppercase tracking-wide">Old account</p>
								<p className="text-[#FFF1E9] font-semibold truncate">{sourceProfile.userName ?? "Anonymous"}</p>
								<div className="flex gap-3 mt-2 text-sm">
									<span className="text-[#E8C5A0]">{sourceProfile.drops} drops</span>
									<span className="text-[#918884]">{sourceProfile.snags} snags</span>
								</div>
							</div>

							<div className="flex items-center text-[#918884] text-xl">→</div>

							<div className="flex-1 bg-[#231F20] rounded-xl p-4 border border-[#E8C5A0]/30">
								<p className="text-xs text-[#E8C5A0] mb-1 uppercase tracking-wide">Keep this one</p>
								<p className="text-[#FFF1E9] font-semibold truncate">{targetProfile.userName ?? "Anonymous"}</p>
								<div className="flex gap-3 mt-2 text-sm">
									<span className="text-[#E8C5A0]">{targetProfile.drops + sourceProfile.drops} drops</span>
									<span className="text-[#918884]">{targetProfile.snags + sourceProfile.snags} snags</span>
								</div>
							</div>
						</div>

						{error && (
							<p role="alert" className="text-red-400 text-sm mb-4 text-center">
								{error}
							</p>
						)}

						<div className="flex justify-end gap-3">
							<button
								onClick={onCancel}
								disabled={isLoading}
								className="px-4 py-2 rounded-lg bg-[#231F20] text-[#FFF1E9] disabled:opacity-50"
							>
								Cancel
							</button>
							<button
								onClick={handleMerge}
								disabled={isLoading}
								className="px-5 py-2 rounded-lg bg-[#E8C5A0] text-[#231F20] font-semibold flex items-center gap-2 disabled:opacity-50"
							>
								{isLoading && (
									<span className="w-4 h-4 border-2 border-[#231F20] border-t-transparent rounded-full animate-spin" />
								)}
								Merge Accounts
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
